import React, { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X, ChevronRight } from "lucide-react";
import { NAV_LINKS, FOOTER_LINKS, BOOKING_URL } from "@/mock";
import { BrandMark, ParticleField, NoiseOverlay } from "@/components/utu/visuals";
import { OutlinePillButton, useStickyShadow } from "@/components/utu/atoms";

export default function Layout({ children }) {
  const stuck = useStickyShadow(12);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const location = useLocation();

  // Close mobile menu on page navigate
  useEffect(() => {
    setMobileMenuOpen(false);
    window.scrollTo({ top: 0, behavior: "instant" });
  }, [location]);

  return (
    <div className="min-h-screen bg-[hsl(var(--utu-bg))] text-white overflow-x-hidden relative">
      {/* Background layers */}
      <div className="pointer-events-none fixed inset-0 z-0">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_20%_10%,rgba(252,211,77,0.10),transparent_45%)]" />
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_85%_18%,rgba(252,211,77,0.07),transparent_55%)]" />
        <ParticleField />
        <NoiseOverlay />
      </div>

      {/* Header */}
      <header
        className={
          "fixed top-0 left-0 w-full z-50 transition-[background-color,box-shadow,border-color] duration-300 " +
          (stuck
            ? "border-b border-white/10 bg-black/45 shadow-[0_18px_60px_rgba(0,0,0,0.55)] backdrop-blur-[24px]"
            : "bg-transparent")
        }
      >
        <div className="mx-auto flex max-w-6xl items-center justify-between gap-6 px-5 py-4 md:px-8">
          <Link to="/" className="flex items-center gap-3">
            <BrandMark />
            <div>
              <div className="text-sm font-semibold tracking-[0.2em] text-white/90">UTU</div>
              <div className="text-[10px] text-white/55 tracking-wider hidden sm:block">AI & TECHNOLOGY LAB</div>
            </div>
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden items-center gap-6 lg:flex">
            {NAV_LINKS.map((l) => {
              const isHash = l.href.startsWith("/#");
              const active = location.pathname === l.href || (isHash && location.pathname === "/");
              return isHash ? (
                <a
                  key={l.id}
                  href={l.href}
                  className={`text-xs font-semibold tracking-wider uppercase transition-colors hover:text-white ${
                    active ? "text-[hsl(var(--utu-gold))]" : "text-white/70"
                  }`}
                >
                  {l.label}
                </a>
              ) : (
                <Link
                  key={l.id}
                  to={l.href}
                  className={`text-xs font-semibold tracking-wider uppercase transition-colors hover:text-white ${
                    active ? "text-[hsl(var(--utu-gold))]" : "text-white/70"
                  }`}
                >
                  {l.label}
                </Link>
              );
            })}
          </nav>

          <div className="hidden lg:flex items-center gap-3">
            <a href={BOOKING_URL} target="_blank" rel="noopener noreferrer">
              <OutlinePillButton className="h-10 px-5 text-xs">
                Audit Consultation
                <ChevronRight className="h-4 w-4" />
              </OutlinePillButton>
            </a>
          </div>

          {/* Mobile Menu Button */}
          <button
            type="button"
            className="inline-flex items-center justify-center p-2 text-white/80 hover:text-white lg:hidden"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            aria-label="Toggle menu"
          >
            {mobileMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </button>
        </div>

        {/* Mobile Menu Dropdown */}
        {mobileMenuOpen && (
          <div className="border-t border-white/10 bg-black/95 px-5 py-6 backdrop-blur-[24px] lg:hidden absolute top-[100%] left-0 w-full shadow-2xl">
            <nav className="flex flex-col gap-4">
              {NAV_LINKS.map((l) => {
                const isHash = l.href.startsWith("/#");
                return isHash ? (
                  <a
                    key={l.id}
                    href={l.href}
                    onClick={() => setMobileMenuOpen(false)}
                    className="text-sm font-semibold tracking-wider uppercase text-white/70 hover:text-white"
                  >
                    {l.label}
                  </a>
                ) : (
                  <Link
                    key={l.id}
                    to={l.href}
                    onClick={() => setMobileMenuOpen(false)}
                    className="text-sm font-semibold tracking-wider uppercase text-white/70 hover:text-white"
                  >
                    {l.label}
                  </Link>
                );
              })}
              <div className="h-px bg-white/10 my-2" />
              <a
                href={BOOKING_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="w-full text-center"
              >
                <OutlinePillButton className="w-full h-11 text-sm justify-center">
                  Audit Consultation
                  <ChevronRight className="h-4 w-4" />
                </OutlinePillButton>
              </a>
            </nav>
          </div>
        )}
      </header>

      {/* Main Content Area */}
      <main className="relative z-10 pt-24 min-h-[calc(100vh-320px)]">
        {children}
      </main>

      {/* Footer */}
      <footer className="relative z-10 border-t border-white/10 bg-black/35 mt-20">
        <div className="mx-auto max-w-6xl px-5 py-12 md:px-8">
          <div className="flex flex-col items-center justify-between gap-6 md:flex-row">
            <Link to="/" className="flex items-center gap-3">
              <BrandMark />
              <div>
                <div className="text-sm font-semibold tracking-[0.18em] text-white/90">UTU</div>
                <div className="text-xs text-white/55">AI & Technology Lab</div>
              </div>
            </Link>
            <div className="flex flex-wrap items-center justify-center gap-x-6 gap-y-2 text-xs text-white/60">
              {FOOTER_LINKS.map((l) => {
                return l.href.startsWith("http") ? (
                  <a key={l.id} href={l.href} target="_blank" rel="noopener noreferrer" className="transition-colors hover:text-white">
                    {l.label}
                  </a>
                ) : (
                  <Link key={l.id} to={l.href} className="transition-colors hover:text-white">
                    {l.label}
                  </Link>
                );
              })}
            </div>
          </div>
          <div className="mt-8 flex flex-col items-center justify-between gap-2 text-[10px] text-white/45 md:flex-row border-t border-white/5 pt-6">
            <div>&copy; {new Date().getFullYear()} UTU. All rights reserved.</div>
            <div className="flex items-center gap-2">
              <span className="inline-flex h-1.5 w-1.5 rounded-full bg-[hsl(var(--utu-gold))]" />
              Sovereign context & AI systems for Africa.
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
